import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
import { useFormTyped } from "shared/hooks/use-form-typed";
import {
  IntegrationId,
  isPmsIntegration,
  Step
} from "views/settings-integration/components/AddIntegrationModal/types";
import { PropertyManagementSystem } from "shared/types/partner";
import { CREDENTIAL_REGISTRY } from "views/settings-integration/integrations/pms/credential-forms/registry";
import * as yup from "yup";

interface CredentialField {
  key: string;
  label: string;
  type?: "text" | "password";
}

const CREDENTIAL_FIELDS_FALLBACK: Partial<
  Record<PropertyManagementSystem | IntegrationId, CredentialField[]>
> = {};

interface CredentialsStepProps {
  selectedIntegration: PropertyManagementSystem | IntegrationId;
  onCredentialsSubmit: (credentials: Record<string, string | boolean>) => void;
  setCurrentStep: (step: Step) => void;
  credentials: Record<string, string | boolean>;
}

export const CredentialsStep = ({
  selectedIntegration,
  onCredentialsSubmit,
  setCurrentStep,
  credentials
}: CredentialsStepProps) => {
  const registryEntry = isPmsIntegration(selectedIntegration)
    ? CREDENTIAL_REGISTRY[selectedIntegration]
    : undefined;
  const fallbackFields = CREDENTIAL_FIELDS_FALLBACK[selectedIntegration] ?? [];

  const initialValues = registryEntry
    ? { ...registryEntry.defaults, ...credentials }
    : fallbackFields.reduce<Record<string, string>>(
        (acc, field) => ({ ...acc, [field.key]: "" }),
        {}
      );

  const validationSchema = registryEntry
    ? registryEntry.schema
    : yup.object(
        fallbackFields.reduce<Record<string, yup.StringSchema>>(
          (acc, field) => ({
            ...acc,
            [field.key]: yup.string().required(`${field.label} is required`)
          }),
          {}
        )
      );

  const form = useFormTyped<Record<string, string | boolean>>(
    initialValues,
    validationSchema
  );

  const handleSubmit = form.onSubmit({
    onSuccess: (values) => onCredentialsSubmit(values)
  });

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <div className="flex flex-col gap-4 overflow-y-auto max-h-[50vh]">
        {registryEntry ? (
          <registryEntry.Form register={form.register} mode="create" />
        ) : (
          fallbackFields.map((field) => (
            <BaseInput
              key={field.key}
              id={field.key}
              label={field.label}
              type={field.type ?? "text"}
              {...form.register(field.key)}
              required
            />
          ))
        )}
      </div>

      <div className="flex gap-2 ml-auto">
        <Button
          type="button"
          variant={ButtonVariant.BASE}
          size={ButtonSize.BASE}
          onClick={() => setCurrentStep(Step.FIRST)}
          className="w-[125px]"
        >
          Back
        </Button>
        <Button
          type="submit"
          variant={ButtonVariant.PRIMARY}
          size={ButtonSize.BASE}
          className="w-[125px]"
        >
          Continue
        </Button>
      </div>
    </form>
  );
};
